import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Heading,
} from "@george-labs.com/design-system"
import {
  LoanCaseDTO,
  LoanCaseStatusDTO,
  ProductInstanceDTO,
} from "@smartx/mikro-vt-client-api"
import { useCallback, useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import { useCaseEventsByCaseId, useLoanCases } from "../../app/hooks"
import { getPalAnnualRevisionKoNotifications } from "../../functions/caseEventsHelper"
import DataList, { IField } from "../../components/DataList/DataList"
import * as dataView from "../../dataview/dataView"
import AuthContext from "../../providers/AuthProvider"
import { CollapsibleComponent, Cvt } from "../../Shared"
import OverviewAccountNumbers from "./OverviewAccountNumbers/OverviewAccountNumbers"
import OverviewAmountsAndDuration from "./OverviewAmountsAndDuration/OverviewAmountsAndDuration"
import OverviewContract from "./OverviewContract/OverviewContract"
import OverviewCustomer from "./OverviewCustomer/OverviewCustomer"
import OverviewDocuments from "./OverviewDocuments/OverviewDocuments"
import OverviewIndividuals from "./OverviewIndividuals/OverviewIndividuals"
import OverviewProduct from "./OverviewProduct/OverviewProduct"
import OverviewStatus from "./OverviewStatus/OverviewStatus"
import OverViewCards from "./OverViewCards/OverViewCards"
import OverviewRealEstateCollaterals from "./OverviewRealEstateCollaterals/OverviewRealEstateCollaterals"
import { productCodes } from "../../functions/caseHelper"
import { VALUE_LIST_ENUM } from "../../functions/setCodeTableValues"
import { isBianco } from "../../functions/helpers"
import StatusBadge from "../../components/StatusBadge/StatusBadge"

const Overview = (_props: any) => {
  const authContext = useContext(AuthContext)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  const cases = useLoanCases()

  const getCaseData = useCallback(() => {
    return cases?.find((item) => item.caseId === caseId)
      ? cases?.find((item) => item.caseId === caseId)
      : ({} as LoanCaseDTO)
  }, [cases, caseId])

  const caseData = getCaseData()

  useEffect(() => {
    authContext.refresh()
  }, [getCaseData, authContext])

  /* Igényelt összeg */
  const getRequestedAmount = () => {
    /* Kétlábas ügylet esetén ez a mező töltve van, így elég ezt visszaadni */
    /* Egylábas ügylet esetén csak egy termék lesz, így elég a nulladik elem requestedAmount értékét visszaadni */
    return caseData.totalRequestedAmount
      ? caseData.totalRequestedAmount
      : caseData.productInstances[0].requestedAmount
  }

  /* Jóváhagyott összeg */
  const getLoanAmount = () => {
    let totalLoanAmount = 0

    /* Minden termék loanAmount értékének az összege */
    if (caseData.productInstances?.length > 0) {
      caseData.productInstances.forEach((product: ProductInstanceDTO) => {
        if (product.loanAmount !== null) {
          totalLoanAmount += product.loanAmount
        }
      })
    }

    /* Ha nem történt meg az összegzés, akkor null-t kell visszaadni */
    return totalLoanAmount === 0 ? null : totalLoanAmount
  }

  const isOnlineRectification = (data: LoanCaseStatusDTO) => {
    return data.onlineInfo?.onlineRectification
  }

  const loanAmount = getLoanAmount()
  const caseEvents = useCaseEventsByCaseId(caseId ?? "")
  const koNotifications = getPalAnnualRevisionKoNotifications(
    caseData?.loanCaseStatus?.status,
    caseData?.caseId,
    caseEvents,
  )

  const fieldArr: Array<IField> = [
    {
      label: t("case.fields.loanCaseStatus"),
      value: (
        <StatusBadge
          loanCaseStatus={caseData?.loanCaseStatus}
          caseId={caseData?.caseId}
        />
      ),
      fieldType: "text",
    },
    {
      label: t("case.fields.cjkNumber"),
      value: caseData?.cjkNumber,
      fieldType: "text",
    },
    loanAmount
      ? {
          label: t("case.fields.loanAmount"),
          value: loanAmount,
          fieldType: "number",
        }
      : null,
    loanAmount
      ? null
      : {
          label: t("case.fields.totalRequestedAmount"),
          value: getRequestedAmount(),
          fieldType: "number",
        },
    caseData?.bookingCode
      ? {
          label: t("case.fields.bookingCode"),
          value: caseData?.bookingCode,
          fieldType: "text",
        }
      : null,
  ]

  return (
    <>
      <Card className="p-2">
        <Heading level={3}>
          {isBianco(caseData?.productCode) ? (
            caseData?.productInstances?.find(Boolean)?.subProductName
          ) : (
            <Cvt
              list={VALUE_LIST_ENUM.ProductList}
              code={caseData?.productCode}
            ></Cvt>
          )}
        </Heading>
        <DataList dataList={fieldArr.filter((field) => field !== null)} />
      </Card>

      {isOnlineRectification(caseData?.loanCaseStatus) && (
        <ButtonGroup className="text-center mt-3 mb-3">
          <Button
            variant={Button.VARIANT.PRIMARY}
            onClick={() => navigate(`/rectification/${caseId}`)}
          >
            {t("rectification.title")}
          </Button>
        </ButtonGroup>
      )}

      {koNotifications?.map((notification, index) => (
        <Alert
          key={`${notification.koMessageSubject}-${index}`}
          heading={notification.koMessageSubject}
          size={Alert.SIZE.LARGE}
          variant={Alert.VARIANT.WARNING}
        >
          {notification.koMessage}
        </Alert>
      ))}

      <Card className="p-2">
        <Heading level={3}>{t("case.title.documents")}</Heading>
        <CollapsibleComponent
          isOpen={true}
          title="case.accordions.uploadedDocuments"
          content={
            <OverviewDocuments
              type="online"
              overviewData={caseData?.documents}
            ></OverviewDocuments>
          }
        ></CollapsibleComponent>
        <CollapsibleComponent
          isOpen={true}
          title="case.accordions.providedDocuments"
          content={
            <OverviewDocuments
              type="branch"
              overviewData={caseData?.documents}
            ></OverviewDocuments>
          }
        ></CollapsibleComponent>
      </Card>

      <Card className="p-2">
        <Heading level={3}>{t("case.accordions.statusData")}</Heading>
        <OverviewStatus loanCaseData={caseData}></OverviewStatus>
      </Card>

      <Card className="p-2">
        <Heading level={3}>{t("case.accordions.customerData")}</Heading>
        <OverviewCustomer customerData={caseData?.customer} />
      </Card>

      <Card className="p-2">
        <Heading level={3}>{t("case.accordions.individualsData")}</Heading>
        <OverviewIndividuals
          individualData={caseData?.individuals}
        ></OverviewIndividuals>
      </Card>

      <Card className="p-2">
        <Heading level={3}>{t("case.accordions.amountsAndDuration")}</Heading>
        <OverviewAmountsAndDuration loanCaseData={caseData} />
      </Card>

      {dataView.isAccordionVisibleByStatus(caseData) && (
        <Card className="p-2">
          <Heading level={3}>{t("case.accordions.contractData")}</Heading>
          <OverviewContract loanCaseData={caseData} />
        </Card>
      )}

      <OverviewProduct loanCaseData={caseData} />

      <Card className="p-2">
        <Heading level={3}>{t("case.accordions.accountNumbersData")}</Heading>
        <OverviewAccountNumbers loanCaseData={caseData} />
      </Card>

      {[productCodes.SZKFH, productCodes.ASZKFH].includes(
        caseData?.productCode,
      ) && (
        <Card className="p-2">
          <Heading level={3}>{t("case.accordions.cardData")}</Heading>
          <OverViewCards loanCaseData={caseData} />
        </Card>
      )}

      {[productCodes.BSZKEH, productCodes.ASZKFH].includes(
        caseData?.productCode,
      ) && (
        <Card className="p-2">
          <Heading level={3}>{t("case.accordions.collateralsData")}</Heading>
          <OverviewRealEstateCollaterals loanCaseData={caseData} />
        </Card>
      )}

      <ButtonGroup className="text-center" style={{ marginBottom: "3.5rem" }}>
        <Button
          variant={Button.VARIANT.SECONDARY}
          onClick={() => navigate("/dashboard")}
        >
          {t("rectification.button.backToDashboard")}
        </Button>
      </ButtonGroup>
    </>
  )
}

export default Overview
